import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  CloudIcon,
  UserCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { chat } from '../services/api';
import { getAssistantErrorMessage, normalizeAssistantSuggestions } from '../utils/platformAssistant';

export function getAssistantSessionId(storage = sessionStorage) {
  let sessionId = storage.getItem('chat-session-id');
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    storage.setItem('chat-session-id', sessionId);
  }
  return sessionId;
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'system',
      content: 'Hi! I can help you manage cloud resources, analyze costs, and handle access permissions. Try asking me to:',
      suggestions: [
        'Add user@example.com to Azure subscription',
        'Show AWS costs for last week',
        'Compare cloud pricing for 4 CPU instances',
        'List all running GCP resources',
      ],
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Chat mutation
  const sendMessage = useMutation(
    async (message) => {
      const response = await chat.send(message, {
        previousMessages: messages.slice(-5), // Send last 5 messages for context
        timestamp: new Date().toISOString(),
        sessionId: getAssistantSessionId(),
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        setIsTyping(false);
        
        // Add AI response
        const aiMessage = {
          id: Date.now(),
          type: 'ai',
          content: data.message || data.result,
          provider: data.provider,
          intent: data.intent,
          confidence: data.confidence,
          followUpSuggestions: data.followUpSuggestions,
          suggestions: normalizeAssistantSuggestions(data.suggestions),
        };
        
        setMessages((prev) => [...prev, aiMessage]);

        // Show success/error toast based on response
        if (data.success) {
          if (data.requiresApproval) {
            toast('Action requires approval', { icon: '⚠️' });
          }
        } else if (data.clarificationNeeded) {
          toast('Need more information', { icon: '💭' });
        }
      },
      onError: (error) => {
        setIsTyping(false);
        
        const errorMessage = {
          id: Date.now(),
          type: 'error',
          content: getAssistantErrorMessage(error),
        };
        
        setMessages((prev) => [...prev, errorMessage]);
        toast.error('Failed to process request');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || sendMessage.isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    sendMessage.mutate(input);
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  const handleNavigationSuggestionClick = (href) => {
    if (!href) return;
    window.location.assign(href);
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-700 transition-all duration-200 z-40 ${
          isOpen ? 'hidden' : ''
        }`}
      >
        <ChatBubbleLeftRightIcon className="h-6 w-6" />
      </motion.button>

      {/* Chat Widget */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 w-96 h-[600px] bg-gray-900 rounded-lg shadow-2xl border border-gray-800 flex flex-col z-50"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                  <SparklesIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">AI Assistant</h3>
                  <p className="text-xs text-gray-400">Cloud Operations & Access</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[80%] ${
                      message.type === 'user'
                        ? 'bg-primary-600 text-white'
                        : message.type === 'error'
                        ? 'bg-red-900/50 text-red-200 border border-red-800'
                        : 'bg-gray-800 text-gray-100'
                    } rounded-lg p-3 ${
                      message.type === 'system' ? 'w-full' : ''
                    }`}
                  >
                    {/* Message content */}
                    <div className="text-sm whitespace-pre-wrap">{message.content}</div>

                    {/* Provider badge */}
                    {message.provider && (
                      <div className="mt-2 flex items-center space-x-2">
                        <CloudIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-xs text-gray-400 uppercase">
                          {message.provider}
                        </span>
                        {message.confidence && (
                          <span className="text-xs text-gray-500">
                            ({Math.round(message.confidence * 100)}% confident)
                          </span>
                        )}
                      </div>
                    )}

                    {/* Suggestions */}
                    {message.suggestions && (
                      <div className="mt-3 space-y-2">
                        {message.suggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() =>
                              suggestion.href
                                ? handleNavigationSuggestionClick(suggestion.href)
                                : handleSuggestionClick(suggestion.label || suggestion)
                            }
                            className="block w-full text-left text-xs p-2 bg-gray-700/50 hover:bg-gray-700 rounded transition-colors"
                          >
                            {suggestion.label || suggestion}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Follow-up suggestions */}
                    {message.followUpSuggestions && message.followUpSuggestions.length > 0 && (
                      <div className="mt-3 space-y-1">
                        <p className="text-xs text-gray-400 mb-1">You might also want to:</p>
                        {message.followUpSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="block w-full text-left text-xs p-2 bg-gray-700/30 hover:bg-gray-700/50 rounded transition-colors"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-100" />
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-200" />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-gray-800">
              <div className="flex items-center space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about cloud resources, costs, or access..."
                  className="flex-1 bg-gray-800 text-white placeholder-gray-400 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  disabled={sendMessage.isLoading}
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sendMessage.isLoading}
                  className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
